import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { FinanceCompany } from '../types';
import { financeCompaniesAPI } from '../services/api';

type CompanyFormState = {
  name: string;
  country: string;
  city: string;
  address: string;
};

const FinanceCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<FinanceCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<FinanceCompany | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<CompanyFormState>({
    name: '',
    country: 'Srbija',
    city: '',
    address: ''
  });

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const data = await financeCompaniesAPI.getAll();
      setCompanies(data);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri učitavanju firmi';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDialog = () => {
    setEditingCompany(null);
    setFormData({
      name: '',
      country: 'Srbija',
      city: '',
      address: ''
    });
    setError('');
    setDialogVisible(true);
  };

  const openEditDialog = (company: FinanceCompany) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || '',
      country: company.country || 'Srbija',
      city: company.city || '',
      address: company.address || ''
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return setError('Naziv je obavezan');
    if (!formData.country.trim()) return setError('Država je obavezna');
    if (!formData.city.trim()) return setError('Grad je obavezan');
    if (!formData.address.trim()) return setError('Adresa je obavezna');

    try {
      setError('');
      if (editingCompany) {
        await financeCompaniesAPI.update(editingCompany._id, formData);
      } else {
        await financeCompaniesAPI.create(formData);
      }
      setDialogVisible(false);
      await loadCompanies();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri čuvanju firme';
      setError(msg);
    }
  };

  const handleDelete = (company: FinanceCompany) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete firmu "${company.name}"?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await financeCompaniesAPI.delete(company._id);
          await loadCompanies();
        } catch (err: any) {
          const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Greška pri brisanju firme';
          setError(msg);
        }
      }
    });
  };

  const actionsBodyTemplate = (rowData: FinanceCompany) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => openEditDialog(rowData)}
          aria-label="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => handleDelete(rowData)}
          aria-label="Obriši"
        />
      </div>
    );
  };

  const dialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Otkaži"
        className="p-button-text"
        onClick={() => setDialogVisible(false)}
      />
      <Button
        label={editingCompany ? 'Sačuvaj' : 'Kreiraj'}
        icon={editingCompany ? 'pi pi-check' : 'pi pi-plus'}
        className="p-button-success"
        onClick={handleSubmit}
      />
    </div>
  );

  return (
    <div className="p-4">
      <ConfirmDialog />

      <Card title="Firme">
        <div className="flex justify-content-start mb-3">
          <Button
            label="Dodaj firmu"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={openCreateDialog}
          />
        </div>

        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <DataTable
          value={companies}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          responsiveLayout="scroll"
          emptyMessage="Nema podataka"
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="country" header="Država" sortable />
          <Column field="city" header="Grad" sortable />
          <Column field="address" header="Adresa" />
          <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editingCompany ? 'Izmeni firmu' : 'Nova firma'}
        visible={dialogVisible}
        style={{ width: '720px', maxWidth: '95vw' }}
        onHide={() => setDialogVisible(false)}
        footer={dialogFooter}
        modal
      >
        {error && (
          <div className="mb-3">
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label htmlFor="company-name">Naziv *</label>
            <InputText
              id="company-name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="company-country">Država *</label>
            <InputText
              id="company-country"
              value={formData.country}
              onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))}
            />
          </div>

          <div className="field col-12 md:col-6">
            <label htmlFor="company-city">Grad *</label>
            <InputText
              id="company-city"
              value={formData.city}
              onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
            />
          </div>

          <div className="field col-12">
            <label htmlFor="company-address">Adresa *</label>
            <InputText
              id="company-address"
              value={formData.address}
              onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FinanceCompanies;


